import { useState } from "react";
import axios from "axios";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, Sparkles, Bug, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicAxios = axios.create({ baseURL: `${BACKEND}/api` });

const KINDS = [
  { value: "feature", label: "Feature request", Icon: Lightbulb, blurb: "Something you wish Oiiister did" },
  { value: "bug", label: "Bug report", Icon: Bug, blurb: "Something's broken or looks wrong" },
  { value: "feedback", label: "General feedback", Icon: Sparkles, blurb: "Anything else on your mind" },
];

export default function ContactUsModal({ open, onOpenChange }) {
  const [kind, setKind] = useState("feature");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setKind("feature");
    setMessage("");
    setEmail("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Give us a little more detail.");
      return;
    }
    setSubmitting(true);
    try {
      await publicAxios.post("/contact", {
        kind,
        message: message.trim(),
        email: email.trim() || null,
      });
      toast.success("Thanks — we read every message.");
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send. Try again in a moment.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="contact-modal"
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl">Report / Request</SheetTitle>
          <SheetDescription>
            Request a feature, report a bug, or just say hi. We read everything.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
              What's this about?
            </Label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {KINDS.map(({ value, label, Icon, blurb }) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`contact-kind-${value}`}
                  onClick={() => setKind(value)}
                  className={`flex items-start gap-3 rounded-2xl px-4 py-3 text-left ring-1 transition-colors ${
                    kind === value
                      ? "bg-[#D6FFEB] ring-[#3FFFB6]"
                      : "bg-white ring-[#E5E7EB] hover:ring-[#3FFFB6]"
                  }`}
                >
                  <span
                    className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      kind === value
                        ? "bg-[#3FFFB6] text-[#111827]"
                        : "bg-[#F3F4F6] text-[#4B5563]"
                    }`}
                  >
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#111827]">{label}</p>
                    <p className="text-xs text-[#4B5563] mt-0.5">{blurb}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="contact-message" className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
              Your message
            </Label>
            <Textarea
              id="contact-message"
              data-testid="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                kind === "bug"
                  ? "What went wrong, and what did you expect to happen?"
                  : kind === "feature"
                  ? "Describe what you wish Oiiister could do…"
                  : "Tell us what's on your mind…"
              }
              className="mt-2 min-h-36 rounded-2xl"
            />
          </div>

          <div>
            <Label htmlFor="contact-email" className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
              Your email <span className="text-[#9CA3AF] normal-case font-normal">(optional — only if you'd like a reply)</span>
            </Label>
            <Input
              id="contact-email"
              data-testid="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 h-11 rounded-2xl"
            />
          </div>

          <Button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="w-full h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold"
          >
            <Send size={16} className="mr-2" />
            {submitting ? "Sending…" : "Send"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
